import React, { useState } from 'react';
import MeetingStoreViewProsAndCons from 'Pages/dashboard/store/Payments/MeetingStoreViewProsAndCons';
import CustomModal from 'components/Modal/CustomModal';
import { PROS_CONS_TOGGLE } from 'utils/constants';
import './MeetingTakeaways.css';

/**
 * ViewProsAndCons Component - Displays individual pros/cons row with modal functionality
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.dataList - The data object for this row
 * @param {string} props.type - The type of data ('pros' or 'cons')
 * @param {string} props.userType - The user type ('host' or 'guest')
 * @return {ReactNode} A React element that renders a clickable pros/cons row
 */
const ViewProsAndCons = ({ dataList, type, userType }) => {
  const [show, setShow] = useState(false);
  const [itemId, setItemId] = useState(null);
  /**
   * Close the modal and reset item ID
   *
   * @return {void}
   */
  const handleClose = () => {
    setShow(false);
    setItemId(null);
  };

  /**
   * Get the appropriate text based on type
   *
   * @return {string} The text to display for this item
   */
  const getText = () => {
    if (type === PROS_CONS_TOGGLE.PROS) {
      return dataList?.pro_text || dataList?.text;
    } else {
      return dataList?.con_text || dataList?.text;
    }
  };

  /**
   * Get the appropriate ID based on type
   *
   * @return {string|number} The ID for this item
   */
  const getId = () => {
    if (type === PROS_CONS_TOGGLE.PROS) {
      return dataList?.pro_id || dataList?.id;
    } else {
      return dataList?.con_id || dataList?.id;
    }
  };

  /**
   * Get the CSS class name for the product category display
   *
   * @param {string} categoryName - The product category name
   * @return {string} The display text for product category
   */
  const getProductCategoryDisplay = categoryName => {
    if (categoryName) {
      return categoryName;
    } else {
      return 'N/A';
    }
  };

  /**
   * Get the user type display text
   *
   * @param {string} userTypeParam - The user type ('host' or 'guest')
   * @return {string} The capitalized user type
   */
  const getUserTypeDisplay = userTypeParam => {
    if (userTypeParam === 'host') {
      return 'Host';
    } else {
      return 'Guest';
    }
  };

  /**
   * Get the pros/cons type display text
   *
   * @param {string} typeParam - The type ('pros' or 'cons')
   * @return {string} The capitalized type
   */
  const getTypeDisplay = typeParam => {
    if (typeParam === PROS_CONS_TOGGLE.PROS) {
      return 'Pros';
    } else {
      return 'Cons';
    }
  };

  /**
   * Handle row click to show modal - follows naming convention
   *
   * @return {void}
   */
  const _handleRowClick = () => {
    setShow(true);
    setItemId(getId());
  };

  return (
    <>
      <tr onClick={_handleRowClick} className='meeting-takeaway-row'>
        <td>
          <div className="d-flex align-items-center">
            <div className="product-name">
              <span>{getText()}</span>
            </div>
          </div>
        </td>
        <td>
          <div className="d-flex align-items-center">
            <div className="product-name">
              <span>{dataList?.count}</span>
            </div>
          </div>
        </td>
        <td>
          <div className="d-flex align-items-center">
            <div className="product-name">
              <span>{getProductCategoryDisplay(dataList?.product_category_name)}</span>
            </div>
          </div>
        </td>
      </tr>
      <CustomModal
        showStatus={show}
        clickStatus={handleClose}
        buttonMessage="Close"
        closeStatus={handleClose}
        title={<div className="takeaway-header">{getUserTypeDisplay(userType)} {getTypeDisplay(type)} meetings</div>}
        message={<MeetingStoreViewProsAndCons itemId={itemId} type={type} userType={userType} />}
        secondMessage="noClose"
        secondButton={false}
        modalClass="demo-details-modal"
        largeModal={true}
      />
    </>
  );
};

export default ViewProsAndCons;
